/**
 * NetobsPopoutView — the entire window UI when the page is loaded with
 * `?popout=1`. No game canvas, no Colyseus connection, no Phaser. Just
 * the netobs HUD strips + inspector panels, mirroring the parent
 * window's data via BroadcastChannel.
 *
 * Layout: tick badge top-left, packet sparkline top-right, packet log
 * + latency heatmap stacked in the body. The popout is standalone — it does
 * NOT run the window engine, so it renders the netobs panel CONTENT components
 * (Phase 3 fold) directly inside fixed-position cards (no toggle gating; the
 * popout IS netobs by definition, always-on).
 *
 * The popout DOM must be visible immediately so the user knows the
 * window is alive — header strip shows "🪟 Netobs Popout · connected
 * via BroadcastChannel · last update Xs ago".
 */

import React, { useEffect, useState } from 'react';
import { isPopoutMode } from '../systems/netobs/popout/popoutMode';
import { mountPopoutShim, popoutShim } from '../systems/netobs/popout/popoutShimState';
import { NetobsTickBadgeContent } from './netobs/NetobsTickBadge';
import { NetobsPacketSparklineContent } from './netobs/NetobsPacketSparkline';
import { NetobsPacketLogPanelContent } from './panels/NetobsPacketLogPanel';
import { NetobsLatencyHeatmapPanelContent } from './panels/NetobsLatencyHeatmapPanel';

const HeaderBar: React.FC = () => {
  const [lastSeen, setLastSeen] = useState<number | null>(null);

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const t = popoutShim.getTick();
      const tr = popoutShim.getTraffic();
      const l = popoutShim.getLatency();
      // Use the most recent receivedAt across all three streams
      const latest = Math.max(
        t?.receivedAt ?? 0,
        tr?.receivedAt ?? 0,
        l?.receivedAt ?? 0,
      );
      setLastSeen(latest > 0 ? Math.round((now - latest) / 1000) : null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const isStale = lastSeen !== null && lastSeen > 5;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 32,
        background: 'rgba(8, 8, 15, 0.95)',
        borderBottom: '1px solid #2a2f3a',
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 12,
        zIndex: 10000,
      }}
    >
      <span style={{ color: '#7af', letterSpacing: '0.05em' }}>🪟 NETOBS POPOUT</span>
      <span style={{ color: '#666' }}>
        connected via BroadcastChannel ('helix-netobs')
      </span>
      <span style={{ marginLeft: 'auto', color: isStale ? '#ff5f5f' : '#42d77d' }}>
        {lastSeen === null
          ? 'waiting for first frame from parent…'
          : isStale
            ? `stale (${lastSeen}s ago — parent window closed?)`
            : `last frame ${lastSeen}s ago`}
      </span>
    </div>
  );
};

/**
 * Force the dev/IPC flags ON in the popout window so the user doesn't have to
 * also pass ?netobs=1 — the popout IS netobs by definition. Downstream gates
 * (`hasDebugLabelOptIn()` in multiplayerDiagnostics, the debug surfaces keyed
 * off `window.forceDebug`) must see this set before they first read it.
 *
 * Invoked synchronously from the NetobsPopoutView render body, BEFORE any
 * child panel renders, so a child gate's first read already observes it.
 *
 * DO NOT move this back to module scope: `src/main.tsx` statically imports
 * this module, so an unguarded module-scope write leaked
 * `window.forceDebug = true` into every main-app page load and silently
 * re-enabled opt-in-only surfaces in every Playwright run (U4b, 2026-09-28 —
 * the U4 multiplayer diagnostic labels came back on). The `isPopoutMode()`
 * guard is the whole contract: only the `?popout=1` window opts in.
 */
function enablePopoutDebugFlags(): void {
  if (typeof window === 'undefined') return;
  if (!isPopoutMode()) return;
  (window as any).forceDebug = true;
}

export const NetobsPopoutView: React.FC = () => {
  // Popout-only, and before the children below mount/render.
  enablePopoutDebugFlags();

  // Mount the BroadcastChannel listener once on first render. Listener
  // populates popoutShimState which all the panels read from.
  useEffect(() => {
    const off = mountPopoutShim();
    return off;
  }, []);

  return (
      <div
        data-testid="netobs-popout-view"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0a14 0%, #14141e 100%)',
          color: '#ddd',
          fontFamily: 'monospace',
          overflow: 'hidden',
        }}
      >
        <HeaderBar />
        {/* TickBadge keeps its own position:fixed. PacketSparkline lost
         *  its self-positioning during the 2026-05-12 DebugLauncher
         *  refactor, so we wrap the Content in a fixed-position card
         *  here — popouts aren't routed through DebugHUDHost. */}
        <div
          style={{
            position: 'fixed',
            top: 48,
            left: 12,
            zIndex: 9998,
            background: 'rgba(8, 8, 15, 0.85)',
            border: '1px solid #2a2f3a',
            borderRadius: 4,
            minWidth: 150,
          }}
        >
          <NetobsTickBadgeContent />
        </div>
        <div
          style={{
            position: 'fixed',
            top: 48,
            right: 12,
            zIndex: 9998,
            background: 'rgba(8, 8, 15, 0.88)',
            border: '1px solid #2a2f3a',
            borderRadius: 4,
            minWidth: 244,
          }}
        >
          <NetobsPacketSparklineContent />
        </div>
        {/* Panel CONTENT (Phase 3 fold) wrapped in fixed-position cards —
            the popout has no window engine to host them. */}
        <div
          style={{
            position: 'fixed',
            top: 120,
            left: 12,
            zIndex: 9997,
            width: 520,
            maxHeight: '40vh',
            overflow: 'auto',
            background: 'rgba(8, 8, 15, 0.88)',
            border: '1px solid #2a2f3a',
            borderRadius: 4,
          }}
        >
          <NetobsPacketLogPanelContent />
        </div>
        <div
          style={{
            position: 'fixed',
            bottom: 12,
            left: 12,
            zIndex: 9997,
            width: 480,
            maxHeight: '40vh',
            overflow: 'auto',
            background: 'rgba(8, 8, 15, 0.88)',
            border: '1px solid #2a2f3a',
            borderRadius: 4,
          }}
        >
          <NetobsLatencyHeatmapPanelContent />
        </div>
      </div>
  );
};
